import type { PermissionGroupSummary } from './mockPermissions';

const SESSION_RECORD_KEY = 'conference-admin:session';
const ACCESS_TOKEN_KEY = 'conference-admin:access-token';
const ACCESS_EXPIRES_AT_KEY = 'conference-admin:access-token-expires-at';
const CURRENT_USER_KEY = 'conference-admin:current-user';
const SESSION_IDENTITY_KEY = 'conference-admin:session-identity';
const LEGACY_REFRESH_TOKEN_KEY = 'conference-admin:refresh-token';
const LEGACY_REFRESH_EXPIRES_AT_KEY =
  'conference-admin:refresh-token-expires-at';

export interface CurrentUser {
  id?: string;
  userid?: string;
  username?: string;
  name?: string;
  nickName?: string;
  avatar?: string;
  email?: string;
  phone?: string;
  wechat?: string;
  title?: string;
  access?: 'admin' | 'user';
  status?: number;
  isFirstLogin?: boolean;
  permissionGroups?: PermissionGroupSummary[];
}

type PermissionGroupSummaryInput = Omit<PermissionGroupSummary, 'id'> & {
  id?: number | string | null;
};

export type CurrentUserInput = Omit<
CurrentUser,
'id' | 'userid' | 'permissionGroups'
> & {
  id?: number | string | null;
  userid?: number | string | null;
  permissionGroups?: PermissionGroupSummaryInput[];
};

export interface AuthSession {
  accessToken: string;
  accessTokenExpiresAt?: number;
  currentUser?: CurrentUser;
}

export interface AuthSessionInput {
  accessToken: string;
  expiresIn?: number | string;
  accessTokenExpiresAt?: number | string;
  currentUser?: CurrentUserInput;
}

let memorySession: Partial<AuthSession> = {};
let memoryCurrentUser: CurrentUser | undefined;
let memoryIdentity: string | undefined;
const sessionEvents = new EventTarget();
let authSessionGeneration = 0;
export const getAuthSessionGeneration = () => {
  synchronizeAuthSession();
  return authSessionGeneration;
};
export const subscribeAuthSession = (listener: () => void) => {
  sessionEvents.addEventListener('change', listener);
  return () => sessionEvents.removeEventListener('change', listener);
};

const canUseStorage = () =>
  typeof window !== 'undefined' && Boolean(window.localStorage);

const getStorage = () => (canUseStorage() ? window.localStorage : undefined);

const parseTimestamp = (value?: number | string) => {
  if (typeof value === 'number') {
    return value;
  }
  if (!value) {
    return undefined;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const deriveExpiresAt = (
  expiresIn?: number | string,
  explicit?: number | string,
) => {
  const parsed = parseTimestamp(explicit);
  if (parsed) {
    return parsed;
  }
  const numericExpiresIn = Number(expiresIn);
  if (Number.isFinite(numericExpiresIn)) {
    return Date.now() + numericExpiresIn * 1000;
  }
  return undefined;
};

const writeStorage = (key: string, value: string | number | undefined) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  if (value === undefined || value === '') {
    storage.removeItem(key);
    return;
  }
  storage.setItem(key, String(value));
};

const readStorage = (key: string) => getStorage()?.getItem(key) || undefined;

const readNumberStorage = (key: string) => {
  const value = readStorage(key);
  if (!value) {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const isExpired = (expiresAt?: number, skewMs = 30_000) =>
  typeof expiresAt === 'number' && expiresAt <= Date.now() + skewMs;

const normalizeId = (id?: number | string | null) =>
  (id === undefined || id === null || id === '' ? undefined : String(id));

const normalizePermissionGroups = (groups?: PermissionGroupSummaryInput[]) =>
  groups?.map((group) => ({
    ...group,
    id: normalizeId(group.id),
  }));

export const normalizeCurrentUser = (
  user?: CurrentUserInput,
): CurrentUser | undefined => {
  if (!user) {
    return undefined;
  }
  const id = normalizeId(user.id);
  const userid = normalizeId(user.userid);
  const username = user.username || userid || user.name;
  const displayName = user.nickName || user.name || username;
  return {
    ...user,
    id,
    username,
    userid: userid || id || username,
    name: displayName,
    nickName: user.nickName || displayName,
    avatar: user.avatar || '/logo.png',
    access: user.access ?? (username === 'admin' ? 'admin' : 'user'),
    permissionGroups: normalizePermissionGroups(user.permissionGroups),
  };
};

const readCurrentUser = () => {
  const raw = readStorage(CURRENT_USER_KEY);
  if (!raw) return undefined;
  try {
    return normalizeCurrentUser(JSON.parse(raw) as CurrentUserInput);
  } catch {
    return undefined;
  }
};

const readSessionRecord = () => {
  const raw = readStorage(SESSION_RECORD_KEY);
  if (raw === undefined) {
    return {
      accessToken: readStorage(ACCESS_TOKEN_KEY),
      currentUser: readCurrentUser(),
      identity: readStorage(SESSION_IDENTITY_KEY),
      accessTokenExpiresAt: readNumberStorage(ACCESS_EXPIRES_AT_KEY),
    };
  }
  try {
    const record = JSON.parse(raw) as {
      schema?: number;
      identity?: string;
      session?: AuthSessionInput | null;
    };
    if (
      record.schema !== 1 ||
      !record.session ||
      typeof record.session.accessToken !== 'string'
    ) return {};
    return {
      accessToken: record.session.accessToken,
      currentUser: normalizeCurrentUser(record.session.currentUser),
      identity:
        typeof record.identity === 'string' ? record.identity : undefined,
      accessTokenExpiresAt: parseTimestamp(record.session.accessTokenExpiresAt),
    };
  } catch {
    return {};
  }
};
const writeSessionRecord = () => {
  writeStorage(
    SESSION_RECORD_KEY,
    JSON.stringify({
      schema: 1,
      identity: memoryIdentity,
      session: memorySession.accessToken ? memorySession : null,
    }),
  );
};

/** One localStorage value commits the token, actor and login identity atomically. */
export const synchronizeAuthSession = () => {
  if (!getStorage()) return;
  const { accessToken, currentUser, identity, accessTokenExpiresAt } =
    readSessionRecord();
  const changedOwner =
    Boolean(accessToken) !== Boolean(memorySession.accessToken) ||
    identity !== memoryIdentity ||
    currentUser?.id !== memoryCurrentUser?.id;
  const changedToken = accessToken !== memorySession.accessToken;
  memoryIdentity = identity;
  memoryCurrentUser = currentUser;
  memorySession = accessToken
    ? { accessToken, accessTokenExpiresAt, currentUser }
    : {};
  if (changedOwner) authSessionGeneration += 1;
  if (changedOwner || changedToken) sessionEvents.dispatchEvent(new Event('change'));
};

export const getStoredCurrentUser = () => {
  synchronizeAuthSession();
  return memoryCurrentUser;
};

export const setStoredCurrentUser = (user?: CurrentUserInput) => {
  synchronizeAuthSession();
  const normalized = normalizeCurrentUser(user);
  if (
    !memorySession.accessToken ||
    (memoryCurrentUser?.id && normalized?.id !== memoryCurrentUser.id)
  ) return;
  const changedOwner = memoryCurrentUser?.id !== normalized?.id;
  memoryCurrentUser = normalized;
  memorySession = { ...memorySession, currentUser: normalized };
  if (memorySession.accessToken) writeSessionRecord();
  writeStorage(
    CURRENT_USER_KEY,
    normalized ? JSON.stringify(normalized) : undefined,
  );
  if (changedOwner) {
    authSessionGeneration += 1;
    sessionEvents.dispatchEvent(new Event('change'));
  }
};

export const setAuthSession = (
  session: AuthSessionInput,
  preserveIdentity = false,
) => {
  const previous = getAuthSession();
  const normalized: AuthSession = {
    accessToken: session.accessToken,
    accessTokenExpiresAt: deriveExpiresAt(
      session.expiresIn,
      session.accessTokenExpiresAt,
    ),
    currentUser: normalizeCurrentUser(session.currentUser),
  };
  const previousActor = previous?.currentUser?.id;
  const nextActor = normalized.currentUser?.id;
  const newLogin =
    !preserveIdentity || !previous || previousActor !== nextActor;
  if (newLogin) authSessionGeneration += 1;
  if (newLogin || !memoryIdentity) memoryIdentity = crypto.randomUUID();
  memorySession = normalized;
  memoryCurrentUser = normalized.currentUser;
  writeSessionRecord();
  writeStorage(ACCESS_TOKEN_KEY, normalized.accessToken);
  writeStorage(ACCESS_EXPIRES_AT_KEY, normalized.accessTokenExpiresAt);
  writeStorage(LEGACY_REFRESH_TOKEN_KEY, undefined);
  writeStorage(LEGACY_REFRESH_EXPIRES_AT_KEY, undefined);
  writeStorage(
    CURRENT_USER_KEY,
    normalized.currentUser ? JSON.stringify(normalized.currentUser) : undefined,
  );
  writeStorage(SESSION_IDENTITY_KEY, memoryIdentity);
  sessionEvents.dispatchEvent(new Event('change'));
  return normalized;
};

export const getAuthSession = (): AuthSession | undefined => {
  synchronizeAuthSession();
  if (!memorySession.accessToken) return undefined;
  return {
    accessToken: memorySession.accessToken,
    accessTokenExpiresAt: memorySession.accessTokenExpiresAt,
    currentUser: memoryCurrentUser,
  };
};

export const hasAuthSession = () => Boolean(getAuthSession()?.accessToken);

/** Random, non-credential login identity, stable across reload and token refresh. */
export const getAuthSessionIdentity = () => {
  if (!hasAuthSession()) return undefined;
  if (!memoryIdentity) {
    memoryIdentity = crypto.randomUUID();
    writeSessionRecord();
    writeStorage(SESSION_IDENTITY_KEY, memoryIdentity);
  }
  return memoryIdentity;
};

export const getAccessToken = () => getAuthSession()?.accessToken;

export const isAccessTokenExpired = () =>
  isExpired(getAuthSession()?.accessTokenExpiresAt);

export const clearAuthSession = () => {
  authSessionGeneration += 1;
  memorySession = {};
  memoryCurrentUser = undefined;
  memoryIdentity = undefined;
  writeSessionRecord();
  [
    ACCESS_TOKEN_KEY,
    ACCESS_EXPIRES_AT_KEY,
    LEGACY_REFRESH_TOKEN_KEY,
    LEGACY_REFRESH_EXPIRES_AT_KEY,
    CURRENT_USER_KEY,
    SESSION_IDENTITY_KEY,
  ].forEach((key) => {
    getStorage()?.removeItem(key);
  });
  sessionEvents.dispatchEvent(new Event('change'));
};

export const createAuthorizationHeader = () => {
  const accessToken = getAccessToken();
  return accessToken ? `Bearer ${accessToken}` : undefined;
};

export const updateAuthSessionTokens = (input: {
  accessToken: string;
  expiresIn?: number | string;
  accessTokenExpiresAt?: number | string;
}) => {
  const current = getAuthSession();
  if (!current) {
    return undefined;
  }
  return setAuthSession(
    {
      ...current,
      accessToken: input.accessToken,
      expiresIn: input.expiresIn,
      accessTokenExpiresAt: input.accessTokenExpiresAt,
      currentUser: current.currentUser,
    },
    true,
  );
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (
      event.key === null ||
      [
        SESSION_RECORD_KEY,
        ACCESS_TOKEN_KEY,
        ACCESS_EXPIRES_AT_KEY,
        CURRENT_USER_KEY,
        SESSION_IDENTITY_KEY,
      ].includes(event.key)
    ) {
      synchronizeAuthSession();
    }
  });
}
